import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClipLoader } from "react-spinners";
import remarkGfm from "remark-gfm";
import { FEATURE_ID, GENERAL, KEY, ROUTE } from '../../Common/constant';
import { allowedWebsiteTypes } from '../../Common/data';
import { getAllArticles, getAllNews } from '../../Common/Service/api';
import FooterOne from '../Footer/FooterOne';
import Header from '../Header/HeaderOne';
import { pageTitle } from '../PageTitle';

const NewsArticlesDetail = () => {
    const navigate = useNavigate()
    const { state, pathname } = useLocation()
    const queryClient = useQueryClient();

    let currentSlug = pathname.split("/")
    currentSlug = currentSlug[currentSlug.length - 1]
    let queryCaches = queryClient.getQueryCache().getAll();
    queryCaches = queryCaches.map(query => ({
        key: query.queryKey,
        data: query.state.data
    })).flat()

    const onFormatNewsContent = (contents) => {
        if (contents.length === 0) return ""

        const contentParagraphArray = []

        for (const content of contents) {
            const text = content.children[0].text || "\n\n"
            contentParagraphArray.push(text)
        }

        const result = contentParagraphArray.join("")

        if (contentParagraphArray.length > 0) return result
    }

    const onNavigateToDetail = (type, data) => () => {
        const params = { type, data }
        if (data.slug === currentSlug) {
            window.scrollTo(0, 0)
            return
        }
        if (type === FEATURE_ID.NEWS) navigate(ROUTE.NEWS.DETAIL.replace(":slug", data.slug), { state: params })
        if (type === FEATURE_ID.ARTICLE) navigate(ROUTE.ARTICLES.DETAIL.replace(":slug", data.slug), { state: params })
    }

    const { data: newsResponse, refetch: refetchNews, isLoading: loadingNews } = useQuery({
        queryKey: [KEY.QUERY.ALL_NEWS],
        enabled: false,
        queryFn: getAllNews,
        placeholderData: keepPreviousData,
        staleTime: 1000 * 60
    })

    const { data: articlesResponse, refetch: refetchArticles, isLoading: loadingArticles } = useQuery({
        queryKey: [KEY.QUERY.ALL_ARTICLES],
        enabled: false,
        queryFn: getAllArticles,
        placeholderData: keepPreviousData,
        staleTime: 1000 * 60
    })

    const latestNews = useMemo(() => {
        const store = []

        if (newsResponse) {
            const responseData = newsResponse?.data
            if (responseData.data.length > 0) {
                let count = 0
                for (const itemNews of responseData.data) {
                    const attributes = itemNews.attributes
                    const imageUrl = attributes.cover.data.attributes.url
                    const website = attributes.website
                    const isShownInEGP = allowedWebsiteTypes.includes(String(website).toLowerCase())
                    const obj = {
                        id: itemNews.id,
                        title: attributes.title,
                        date: dayjs(attributes.published_date).format("DD MMMM YYYY"),
                        author: attributes.source,
                        image: imageUrl ? `${GENERAL.BASE_URL}${imageUrl}` : null,
                        slug: attributes.slug,
                        highlight: attributes.highlight,
                        content: onFormatNewsContent(attributes.content),
                        rawContent: attributes.content,
                        category: attributes.category || null,
                        tags: attributes.tags || [],
                        data_aos_delay: `${100 * count}ms`,
                    }
                    if (isShownInEGP) {
                        count++
                        store.push(obj)
                    }
                }
            }
        }

        return store.slice(0, 3)
    }, [newsResponse])

    const latestArticles = useMemo(() => {
        const store = []

        if (articlesResponse) {
            const responseData = articlesResponse?.data
            if (responseData.data.length > 0) {
                let count = 0
                for (const itemArticle of responseData.data) {
                    const attributes = itemArticle.attributes
                    const imageUrl = attributes.cover.data.attributes.url
                    const website = attributes.website
                    const isShownInEGP = allowedWebsiteTypes.includes(String(website).toLowerCase())
                    const obj = {
                        id: itemArticle.id,
                        title: attributes.title,
                        date: dayjs(attributes.published_date).format("DD MMMM YYYY"),
                        author: attributes.author || "Anonymous",
                        image: imageUrl ? `${GENERAL.BASE_URL}${imageUrl}` : null,
                        slug: attributes.slug,
                        highlight: attributes.highlight,
                        content: attributes.content,
                        category: attributes.category,
                        tags: attributes.tags,
                        data_aos_delay: `${100 * count}ms`,
                    }
                    if (isShownInEGP) {
                        count++
                        store.push(obj)
                    }
                }
            }
        }

        return store.slice(0, 3)
    }, [articlesResponse])

    const { title, type, detail } = useMemo(() => {
        const defaultDetail = {
            date: "",
            title: "",
            author: "",
            image: "",
            content: "",
            rawContent: [],
            tags: [],
            category: ""
        }
        let title = ""

        if (state) {
            if (state.type === FEATURE_ID.NEWS) title = "News"
            if (state.type === FEATURE_ID.ARTICLE) title = "Articles"
        }

        return {
            title,
            type: state ? state.type : "",
            detail: state ? state.data : defaultDetail,
        }
    }, [state])

    const content = useMemo(() => {
        if (type === FEATURE_ID.NEWS) return onFormatNewsContent(detail.rawContent)
        else return ""
    }, [type, detail])

    pageTitle(`${title} Details`);

    useEffect(() => {
        if (type) {
            if (queryCaches.length > 0) {
                for (const cache of queryCaches) {
                    if (cache.key.includes(KEY.QUERY.ALL_NEWS)) {
                        if (type === FEATURE_ID.NEWS && !cache.data) refetchNews()
                    }

                    if (cache.key.includes(KEY.QUERY.ALL_ARTICLES)) {
                        if (type === FEATURE_ID.ARTICLE && !cache.data) refetchArticles()
                    }
                }
            } else {
                if (type === FEATURE_ID.NEWS) refetchNews()
                if (type === FEATURE_ID.ARTICLE) refetchArticles()
            }
        }
    }, [type, queryCaches])

    return (
        <>
            <Header />
            <div className="blog__details see__pad">
                <div className="auto-container">
                    <div className="row">
                        <div className="col-xl-12 col-lg-12 col-md-12">
                            <div className="blog__details__content ">
                                <div className="blog__image p_relative">
                                    {detail.image ?
                                        <div style={{ display: "flex", width: "100%", height: "90vh", justifyContent: "center", alignItems: "center", background: "linear-gradient(145deg, #00061A 0%, #060e26 100%)", boxShadow: "0 10px 30px rgba(0,0,0,0.1)", paddingBottom: "99px" }}>
                                            <img src={detail.image} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", filter: "drop-shadow(0 5px 15px rgba(0,0,0,0.08))" }} />
                                        </div>
                                        :
                                        <div style={{ display: "flex", width: "100%", height: "512px", justifyContent: "center", alignItems: "center", background: "#BCBCBC", paddingBottom: "99px" }}>
                                            <svg
                                                fill="#666"
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="60px"
                                                height="60px"
                                                viewBox="0 0 52 52"
                                                enableBackground="new 0 0 52 52"
                                                xmlSpace="preserve"
                                            >
                                                <path d="M50,10c0-2.2-1.8-4-4-4H6c-2.2,0-4,1.8-4,4v32c0,2.2,1.8,4,4,4h40c2.2,0,4-1.8,4-4V10z M39.6,38h-29 c-1.2,0-1.9-1.3-1.3-2.3l8.8-15.3c0.4-0.7,1.3-0.7,1.7,0l5.3,9.1c0.4,0.6,1.3,0.7,1.7,0.1l4.3-6.2c0.4-0.6,1.3-0.6,1.7,0L40.7,36 C41.3,36.9,40.7,38,39.6,38z M37,20c-2.2,0-4-1.8-4-4s1.8-4,4-4s4,1.8,4,4S39.2,20,37,20z" />
                                            </svg>
                                        </div>
                                    }

                                    <div className="detail__post__date">
                                        <ul>
                                            <li> <i className="icon-15"></i>{detail.date || ""}</li>
                                            <li className="two"></li>
                                            <li><i className="icon-09"></i>{detail.author || ""}</li>
                                            {detail.category ?
                                                <>
                                                    <li className="two"></li>
                                                    <li><i className="icon-17"></i>{detail.category || ""}</li>
                                                </>
                                                :
                                                null
                                            }
                                        </ul>
                                    </div>
                                </div>

                                <div className="blog__inner__box">
                                    <h3 className="blog__title ">{detail.title}</h3>
                                    <div className="blog__details__text ">
                                        {/* {type === FEATURE_ID.NEWS &&
                                            <p>{content}</p>
                                        }
                                        {type === FEATURE_ID.ARTICLE &&
                                            <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]}>{detail.content}</ReactMarkdown>
                                            } */}
                                        <ReactMarkdown
                                            remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
                                            components={{
                                                img: ({ node, ...props }) => (
                                                    <img
                                                        {...props}
                                                        className='image-content-markdown'
                                                        alt={props.alt || 'Content image'}
                                                        loading="lazy"
                                                    />
                                                )
                                            }}
                                            className='content-markdown'
                                        >
                                            {type === FEATURE_ID.NEWS ? content : detail.content}
                                        </ReactMarkdown>
                                    </div>
                                </div>

                                {detail.tags.length > 0 ?
                                    <div className="blog__tags__box">
                                        <div className="row">
                                            <div className="col-lg-8 col-md-12 col-sm-12">
                                                <div className="tags__left">
                                                    <h3>Tags:</h3>
                                                    <ul className="tags-list-1">
                                                        {detail.tags.map((tag) => (
                                                            <li key={tag.name}><a>{tag.name}</a></li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    :
                                    null
                                }

                                <div style={{ marginBottom: "40px" }} />

                                <div className="single__sidebar__box">
                                    <div className="title">
                                        <h3 style={{ display: "flex", alignItems: "center", gap: "12px" }}>Latest Posts <ClipLoader color="#ffffff" size={24} loading={loadingNews || loadingArticles} /> </h3>
                                    </div>
                                    <div className="sidebar__blog__post">
                                        <ul className="blog__post">
                                            {type === FEATURE_ID.NEWS && latestNews.length > 0 ?
                                                latestNews.map((news) => (
                                                    <li key={news.id} onClick={onNavigateToDetail(FEATURE_ID.NEWS, news)}>
                                                        <div className="inner">
                                                            <div className="img__box">
                                                                <img src={news.image} alt="" style={{ objectFit: "cover", objectPosition: "center" }} />
                                                            </div>
                                                            <div className="title__box">
                                                                <div className="date"><i className="icon-15"></i>{news.date}</div>
                                                                <h4>{news.title}</h4>
                                                            </div>
                                                        </div>
                                                    </li>
                                                ))
                                                :
                                                null
                                            }

                                            {type === FEATURE_ID.ARTICLE && latestArticles.length > 0 ?
                                                latestArticles.map((article) => (
                                                    <li key={article.id} onClick={onNavigateToDetail(FEATURE_ID.ARTICLE, article)}>
                                                        <div className="inner">
                                                            <div className="img__box">
                                                                <img src={article.image} alt="" style={{ objectFit: "cover", objectPosition: "center" }} />
                                                            </div>
                                                            <div className="title__box">
                                                                <div className="date"><i className="icon-15"></i>{article.date}</div>
                                                                <h4>{article.title}</h4>
                                                            </div>
                                                        </div>
                                                    </li>
                                                ))
                                                :
                                                null
                                            }
                                        </ul>
                                    </div>
                                </div>

                                <div style={{ marginBottom: "128px" }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <FooterOne />
        </>
    );
};

export default NewsArticlesDetail;
